import * as React from "react";
import * as ExpoImagePicker from "expo-image-picker";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import UserAvatar from "@components/UserAvatar";
import LArtwork from "@components/LArtwork";
import { ActivityIndicator } from "react-native";

interface ImagePickerProps {
  title: string;
  imageUrl?: string | null;
  onPick: ({
    uri,
    type,
    base64,
    mimeType,
  }: {
    uri: string;
    type: any;
    base64?: string | null;
    mimeType?: string | null;
  }) => void;
  avatar?: boolean;
  defaultAvatarColors?: [string, string] | null;
  imageCacheKey?: string;
  border?: boolean;
  disabled?: boolean;
  allowsEditing?: boolean;
}

export const pickImage = async (
  onPick: ImagePickerProps["onPick"],
  onCancel?: () => void,
  { allowsEditing = true }: { allowsEditing?: boolean } = {},
) => {
  const result = await ExpoImagePicker.launchImageLibraryAsync({
    mediaTypes: ExpoImagePicker.MediaTypeOptions.Images,
    allowsEditing,
    ...(allowsEditing ? { aspect: [1, 1] as [number, number] } : {}),
    quality: 0.2,
    selectionLimit: 1,
    allowsMultipleSelection: false,
  });

  if (!result.canceled) {
    onPick({
      uri: result.assets?.at(0)?.uri ?? "",
      type: result.assets?.at(0)?.type ?? "",
      base64: result.assets?.at(0)?.base64 ?? "",
      mimeType: result.assets?.at(0)?.mimeType ?? null,
    });
  } else {
    onCancel?.();
  }
};

const ImagePicker = ({
  title,
  imageUrl,
  onPick,
  avatar,
  defaultAvatarColors,
  border,
  disabled = false,
  allowsEditing = true,
}: ImagePickerProps) => {
  const [isOpening, setIsOpening] = React.useState(false);

  const openPicker = async () => {
    if (isOpening) return;
    setIsOpening(true);
    try {
      await pickImage(onPick, undefined, { allowsEditing });
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <>
      {title.length > 0 && (
        <LText variant="h2" mb="s2">
          {title}
        </LText>
      )}

      <LBox alignItems="center" justifyContent="center" mt="s2">
        <LPressable
          sentryEventName="image_picker"
          onPress={openPicker}
          disabled={disabled || isOpening}
        >
          {avatar ? (
            <UserAvatar
              image={imageUrl}
              defaultAvatarColors={defaultAvatarColors}
              userAvatar={true}
              size={120}
              borderRadius="xxl"
              border={border}
            />
          ) : (
            <LArtwork url={imageUrl} imageSize="medium" size={150} />
          )}
          <LBox
            position={"absolute"}
            bottom={0}
            right={0}
            borderWidth={2}
            borderColor="white"
            borderRadius="max"
            padding="s2"
            backgroundColor={
              disabled
                ? "inactiveBg"
                : [
                    "buttonBackgroundPrimaryLeft",
                    "buttonBackgroundPrimaryRight",
                  ]
            }
          >
            {isOpening ? (
              <ActivityIndicator color="white" size={20} />
            ) : (
              <LIcon colors={{ color: "whitePink" }} name="edit" size={20} />
            )}
          </LBox>
        </LPressable>
      </LBox>
    </>
  );
};

export default ImagePicker;
